import React, { useState } from 'react';
import { Collapse, Divider, IconButton, Paper, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useCampaign, useCampaignForm } from 'hooks';
import { BillingMethod, CampaignStep, RouteList } from 'types';
import { HalfSizedContentBlock } from './HalfSizedContentBlock';
import { formatDate, getFormattedChfCurrency, isDiscountActive } from 'utils';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { matchPath, useLocation } from 'react-router-dom';
import { DATE_FORMAT, DISCOUNT_SIDEBAR_MESSAGE1, DISCOUNT_SIDEBAR_MESSAGE2 } from 'shared';
import Lottie from 'lottie-react';
import discountAnimationData from 'assets/animations/discount-animation.json';

export const billingMethodTexts = {
  [BillingMethod.ENGAGEMENT]: 'Preis pro Engagement (Empfohlen)',
  [BillingMethod.CLICK]: 'Preis pro Klick',
};

export const billingMethodNames = {
  [BillingMethod.ENGAGEMENT]: 'Engagements',
  [BillingMethod.CLICK]: 'Klicks',
};

export const RightSidebar = () => {
  const { pathname } = useLocation();
  const theme = useTheme();
  const mediaSmAndUp = useMediaQuery(theme.breakpoints.up('sm'));
  const { watch, getIsStepValid, activeStep } = useCampaignForm();
  const { getCampaignQuery } = useCampaign();
  const [detailsVisible, setDetailsVisible] = useState(false);
  const {
    campaignPeriod: [start, end],
    budget,
    websites,
    billingMethod,
    maxUnitPrice,
    targetingGuidance,
  } = watch();

  const isCreateCampaignPage = !!matchPath({ path: RouteList.CampaignCreate }, pathname);
  const campaignPeriodIsValid = start?.isValid && end?.isValid;
  const timeRange = campaignPeriodIsValid ? Math.round(end?.diff(start, 'days').days) : 1;
  const showDiscountActive = isDiscountActive();

  if (activeStep === CampaignStep.Complete) return null;

  return (
    <Paper sx={{ p: 5 }}>
      <Typography variant="subtitle1" sx={{ display: { xs: 'none', sm: 'block' }, textAlign: 'center' }}>
        Deine Kampagne <br /> auf einen Blick
      </Typography>

      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ display: { xs: 'flex', sm: 'none' } }}
        onClick={() => setDetailsVisible(!detailsVisible)}
      >
        <Stack alignItems="left">
          <Typography variant="h4">Zusammenfassung</Typography>
          <Typography variant="body1">Deine Kampagne auf einen Blick</Typography>
        </Stack>
        <IconButton size="small">{detailsVisible ? <ExpandLess /> : <ExpandMore />}</IconButton>
      </Stack>

      {showDiscountActive && (
        <Stack alignItems="center" sx={{ mt: 4 }}>
          {isCreateCampaignPage && (
            <Stack direction="row" alignItems="center" gap={2} position="relative">
              <Stack position="absolute" top={-150} left={-150}>
                <Lottie animationData={discountAnimationData} style={{ width: 300, height: 300 }} loop={false} />
              </Stack>
            </Stack>
          )}
          <Typography variant="body3" fontWeight={700} color="success.dark" textAlign="center">
            {DISCOUNT_SIDEBAR_MESSAGE1}
            <br />
            {DISCOUNT_SIDEBAR_MESSAGE2}
          </Typography>
        </Stack>
      )}

      <Collapse in={mediaSmAndUp ? true : detailsVisible}>
        <Divider sx={{ my: 4 }} />
        <Stack divider={<Divider sx={{ my: 4 }} />}>
          {(!getIsStepValid(CampaignStep.Basic) || isCreateCampaignPage) && (
            <Typography variant="body3">
              Mache erste Angaben zur Kampagne, um hier die Zusammenfassung zu sehen.
            </Typography>
          )}
          {getIsStepValid(CampaignStep.Basic) && (
            <HalfSizedContentBlock
              title="Dauer"
              description={campaignPeriodIsValid ? `${timeRange} Tage` : 'Datum fehlt'}
              secondLineDescription={
                campaignPeriodIsValid ? `${formatDate(start, DATE_FORMAT)} - ${formatDate(end, DATE_FORMAT)}` : ''
              }
            />
          )}
          {getIsStepValid(CampaignStep.Basic) && (
            <Stack gap={4}>
              <Typography variant="body3" fontWeight={700}>
                So wird deine Kampagne berechnet
              </Typography>
              <HalfSizedContentBlock title="Preismodell" description={billingMethodTexts[billingMethod]} />
              {budget && (
                <HalfSizedContentBlock title="Dein Gesamtbudget" description={getFormattedChfCurrency(budget)} />
              )}
              {maxUnitPrice && (
                <>
                  <HalfSizedContentBlock
                    title="Max. Preis pro Einheit"
                    description={getFormattedChfCurrency(maxUnitPrice)}
                  />
                  {/* temp hidden, needs rework */}
                  {/* <HalfSizedContentBlock title="Berechnetes Ergebnis" description={getCalculationsText} /> */}
                </>
              )}
            </Stack>
          )}

          {getIsStepValid(CampaignStep.Creatives) && (
            <Stack gap={4}>
              {websites.length &&
                websites.map(({ url, creatives }) => (
                  <Stack key={url}>
                    <Typography variant="body3" fontWeight={700} sx={{ wordBreak: 'break-word' }}>
                      {url}
                    </Typography>{' '}
                    <Typography variant="body3">{`${creatives.length} von 6 Creatives hochgeladen`}</Typography>
                  </Stack>
                ))}
            </Stack>
          )}
          {/* update when step is done */}
          {getIsStepValid(CampaignStep.Targeting) && (
            <HalfSizedContentBlock
              title="Zielgruppen"
              description={
                targetingGuidance ? 'Automatisch optimiert und zusätzlicher Vorschlag' : 'Automatisch optimiert'
              }
            />
          )}
          {/* // update text "Verbunden" - is connection is successful */}
          {getIsStepValid(CampaignStep.Basic) && (
            <HalfSizedContentBlock
              title="GA4"
              description={getCampaignQuery.data?.campaign.ga4ConnectionValid ? 'Verbunden' : 'Noch nicht verbunden'}
            />
          )}
        </Stack>
      </Collapse>
    </Paper>
  );
};
